import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Grow,
  FormControl,
  MenuItem,
  Select,
  IconButton,
} from "@mui/material";
import { AccessTimeOutlined, Add, BallotOutlined, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { colors } from "../../../../ui/colors";
import { TimesheetRow } from "../../../interfaces/time-sheet.interfaces";
import EmptyTimesheetState from "../../TimeSheetEmptyState";
import { LoadingRows } from "../../SkeletonTimeSheet";
import { useMemo, useState } from "react";
import AddTimeRow from "./AddTimeRow";
import EditTimeRow from "./EditTimeRow";
import TimesheetTableRow from "./TImeSheetTableRow";
import { useTimeSheetMutation } from "../../../hooks/useTimeSheetMutation";
import { getWorkedStatus } from "../helpers/getWorkedStatus.helpers";
import CardInfo from "../CardInfo";
import { WorkedStatus } from "../enums/workedStatus.enum";
import { formatDuration } from "../helpers/formatduration.helpers";
import { menuPropsModal } from "../../../../../utils";
import { WORK_RECORD_STATUS_FILTERS, WorkRecordStatusFilter } from "../workRecordStatusFilter";

interface Props {
  isGlobal?: boolean;
  timeSheets: TimesheetRow[];
  isLoading?: boolean;
  onSelectWorker?: (worker: any) => void;
  onClearFilters?: () => void;
  pagination: {
    page: number;
    pageSize: number;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    total: number;
  };
  /** Full task count for the active filters. Page rows are not the source of this number. */
  tasksSummary?: {
    count: number;
    label: string;
  };
  workStatus?: WorkRecordStatusFilter;
  onWorkStatusChange?: (status: WorkRecordStatusFilter) => void;
}

export default function TableSheetLogs({
  onSelectWorker,
  isGlobal = true,
  timeSheets = [],
  isLoading = false,
  onClearFilters,
  pagination,
  tasksSummary,
  workStatus = "all",
  onWorkStatusChange,
}: Props) {
  const [showAddRow, setShowAddRow] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);

  const grandTotal = useMemo(() => {
    return timeSheets.reduce((acc, row) => {
      const { totalSeconds } = getWorkedStatus(row);
      return acc + (totalSeconds || 0);
    }, 0);
  }, [timeSheets]);

  const groupedTimeSheets = useMemo(() => {
    if (!isGlobal) return timeSheets;

    const groups: Record<string, any> = {};

    timeSheets.forEach((row) => {
      const workerId = row.workforceUid;
      const { totalSeconds } = getWorkedStatus(row);
      const hasTravel = typeof row.travelSeconds === "number";
      const travelSeconds = hasTravel ? row.travelSeconds : 0;

      if (!groups[workerId]) {
        groups[workerId] = {
          ...row,
          computedTotalSeconds: totalSeconds,
          computedTravelSeconds: hasTravel ? travelSeconds : null,
        };
      } else {
        groups[workerId].computedTotalSeconds += totalSeconds;
        if (hasTravel) {
          groups[workerId].computedTravelSeconds = (groups[workerId].computedTravelSeconds || 0) + travelSeconds;
        }
      }
    });

    return Object.values(groups);
  }, [timeSheets, isGlobal]);

  const { totalHoursCalculated, totalJobsCalculated } = useMemo(() => {
    const statusQueSuman = [
      WorkedStatus.COMPLETED,
      WorkedStatus.COMPLETED_LATE_START,
      WorkedStatus.COMPLETED_OVERTIME,
      WorkedStatus.COMPLETED_LATE,
      WorkedStatus.MISSING_CHECKOUT,
    ];

    return timeSheets.reduce(
      (acc, row) => {
        const { workedStatus, totalSeconds } = getWorkedStatus(row);

        if (statusQueSuman.includes(workedStatus as WorkedStatus)) {
          acc.totalHoursCalculated += totalSeconds || 0;
          acc.totalJobsCalculated += 1;
        }

        return acc;
      },
      {
        totalHoursCalculated: 0,
        totalJobsCalculated: 0,
      },
    );
  }, [timeSheets]);

  const formatedTimeSheets = useMemo(() => {
    return timeSheets.map((row: TimesheetRow) => ({
      jobAlias: row.jobAlias,
      uid: row.uid,
      workforceEventUid: row.workforceEventUid,
      startDate: row.startDate,
      endDate: row.endDate,
      workforceUid: row.workforceUid,
    }));
  }, [timeSheets]);

  const { createTimeSheetMutation: createTimeSheet, updateTimeSheetMutation: updateTimeSheet } = useTimeSheetMutation();

  // no global ahora tiene 8 columnas
  const columnCount = isGlobal ? 3 : 9;

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));

  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;

  const to = Math.min(pagination.page * pagination.pageSize, pagination.total);

  const handleSave = (data: { jobEventUid: string; workforceUid: string; startAt: string; endAt: string }) => {
    createTimeSheet.mutateAsync(data);
    setShowAddRow(false);
  };

  const handleEdit = async (data: { uid: string; startAt: string; endAt: string }) => {
    try {
      console.log("Enviando edición para ID:", data.uid);

      await updateTimeSheet.mutateAsync(data);
    } catch (error) {
      console.error("Error al editar:", error);
    } finally {
      setEditingUid(null);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        width: "100%",
      }}
    >
      <Box display="flex" gap={3} mb={1}>
        <CardInfo
          icon={<AccessTimeOutlined />}
          title={isGlobal ? "COMPANY TOTAL TIME" : "WORKER TOTAL TIME"}
          data={formatDuration(totalHoursCalculated)}
        />

        <CardInfo
          icon={<BallotOutlined />}
          title={tasksSummary?.label ?? (isGlobal ? "COMPLETED PROJECTS" : "COMPLETED TASKS")}
          data={(tasksSummary?.count ?? totalJobsCalculated).toString()}
        />
      </Box>

      <Paper
        sx={{
          borderRadius: 4,
          width: "100%",
          overflow: "hidden",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.03)",
          p: 1,
          minHeight: 450,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 18,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            Timesheet Logs
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            {!isGlobal && onWorkStatusChange && (
              <FormControl size="small">
                <Select
                  MenuProps={menuPropsModal}
                  value={workStatus}
                  onChange={(event) => {
                    onWorkStatusChange(event.target.value as WorkRecordStatusFilter);
                    pagination.setPage(1);
                  }}
                  sx={{
                    minWidth: 170,
                    height: 34,
                    fontSize: 12,
                    fontWeight: 800,
                    borderRadius: 2,
                  }}
                >
                  {WORK_RECORD_STATUS_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {!isGlobal && (
            <Grow in={!isGlobal}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowAddRow(true)}
                sx={{
                  bgcolor: colors.orange.base,
                  "&:hover": {
                    bgcolor: colors.orange[600],
                    boxShadow: "none",
                  },
                  "&:active": {
                    boxShadow: "none",
                  },
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  fontSize: 10,
                  boxShadow: "none",
                }}
              >
                Add Time
              </Button>
            </Grow>
          )}
          </Box>
        </Box>

        {!isGlobal && showAddRow && <AddTimeRow formatedTimeSheets={formatedTimeSheets} onSave={handleSave} onCancel={() => setShowAddRow(false)} />}

        <TableContainer
          sx={{
            flex: 1,
            overflowX: "hidden",
          }}
        >
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
                    color: "var(--fx-surface-texttertiary, #94A3B8)",
                    fontWeight: 800,
                    fontSize: 10,
                    textTransform: "uppercase",
                    px: 0.5,
                    py: 1.5,
                    lineHeight: 1,
                  },
                }}
              >
                {!isGlobal && (
                  <>
                    <TableCell
                      align="center"
                      sx={{
                        width: "19%",
                      }}
                    >
                      Project
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: "20%",
                      }}
                    >
                      Schedule
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: "9%",
                      }}
                    >
                      In
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: "9%",
                      }}
                    >
                      Out
                    </TableCell>

                    {/* columna angosta, sin título */}
                    <TableCell
                      align="center"
                      sx={{
                        width: "5%",
                      }}
                    />

                    <TableCell
                      align="center"
                      sx={{
                        width: "14%",
                      }}
                    >
                      Status
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: "8%",
                      }}
                    >
                      Hours
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: "8%",
                      }}
                    >
                      Travel
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        width: "14%",
                        pr: 1,
                      }}
                    >
                      Actions
                    </TableCell>
                  </>
                )}

                {isGlobal && (
                  <>
                    <TableCell
                      align="left"
                      sx={{
                        pl: 2,
                      }}
                    >
                      Worker
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        pr: 2,
                      }}
                    >
                      Total Time
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        pr: 2,
                      }}
                    >
                      Travel
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                "& td": {
                  px: 0,
                },
              }}
            >
              {isLoading ? (
                <LoadingRows isGlobal={isGlobal} />
              ) : groupedTimeSheets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <EmptyTimesheetState />
                  </TableCell>
                </TableRow>
              ) : (
                groupedTimeSheets.map((row) => {
                  const isEditing = editingUid === row.uid;

                  if (isEditing && !isGlobal) {
                    return (
                      <EditTimeRow
                        key={`edit-${row.uid}`}
                        row={row}
                        formatedTimeSheets={formatedTimeSheets}
                        onSave={(data) => {
                          handleEdit({
                            ...data,
                            uid: row.workforceEventUid,
                          });
                        }}
                        onCancel={() => setEditingUid(null)}
                      />
                    );
                  }

                  return (
                    <TimesheetTableRow
                      key={`${row.uid}-${row.workforceUid ?? "no-worker"}`}
                      row={row}
                      isGlobal={isGlobal}
                      onSelectWorker={onSelectWorker}
                      totalSecondsGrouped={row.computedTotalSeconds}
                      travelSecondsGrouped={row.computedTravelSeconds}
                      onEdit={(id) => setEditingUid(id)}
                      onDelete={(id) =>
                        updateTimeSheet.mutateAsync({
                          uid: id,
                          isDeleted: true,
                        })
                      }
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            pt: 1.5,
            mt: 1.5,
            borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                lineHeight: 1,
                fontSize: 12,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
              }}
            >
              Rows per page
            </Typography>

            <FormControl size="small">
              <Select
                MenuProps={menuPropsModal}
                value={pagination.pageSize}
                onChange={(e) => {
                  pagination.setPageSize(Number(e.target.value));
                  pagination.setPage(1);
                }}
                sx={{
                  minWidth: 80,
                  height: 34,
                  fontSize: 12,
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              >
                {[5, 10, 15, 20, 25, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
              }}
            >
              {from}-{to} of {pagination.total}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() => pagination.setPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
                sx={{
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  borderRadius: 2,
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>

              <Typography
                sx={{
                  minWidth: 40,
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {pagination.page} / {totalPages}
              </Typography>

              <IconButton
                onClick={() => pagination.setPage(pagination.page + 1)}
                disabled={pagination.page >= totalPages}
                sx={{
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  borderRadius: 2,
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
